import { useState, useEffect } from "react";
import axios from "axios";
import { CreditCard, Plus, Edit, Trash2, Eye, Truck, User, Loader2, Search, ChevronDown, ChevronUp, Phone, Building2, ExternalLink, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One column template shared by the agent summary row and its booking rows so they line up
const AGENT_GRID = { gridTemplateColumns: 'minmax(110px,1.3fr) minmax(80px,0.9fr) minmax(160px,1.8fr) minmax(130px,1.4fr) minmax(100px,1.1fr) minmax(88px,1fr) minmax(80px,0.9fr) minmax(80px,0.9fr) minmax(68px,0.8fr) minmax(56px,0.7fr) 24px', columnGap: '12px' };

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatCurrency = (value) => {
  // Round-off float residue from summed payments can leave a tiny negative
  // (or JS's own -0) even when the true balance is zero — normalize before
  // formatting so the UI never shows a misleading "₹-0".
  let n = Math.round((Number(value) || 0) * 100) / 100;
  if (n === 0) n = 0;
  return `₹${n.toLocaleString()}`;
};

const paymentModes = [
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "credit_card", label: "Credit Card" },
  { value: "debit_card", label: "Debit Card" },
  { value: "cash", label: "Cash" }
];

const AdminPayments = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("agent");
  const [payments, setPayments] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agentPaymentFilter, setAgentPaymentFilter] = useState("all"); // all|unpaid|partial|paid
  const [expandedAgentKey, setExpandedAgentKey] = useState(null);
  const [expandedVendorKey, setExpandedVendorKey] = useState(null);
  const [expandedBookingKeys, setExpandedBookingKeys] = useState(new Set());
  
  // Forms
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editingVendorPayment, setEditingVendorPayment] = useState(null);
  const [savingAgentPayment, setSavingAgentPayment] = useState(false);
  const [savingVendorPayment, setSavingVendorPayment] = useState(false);
  const [vendorSearch, setVendorSearch] = useState("");
  // ── Agent download dialog ────────────────────────────────────────
  const [showAgentDlDialog, setShowAgentDlDialog] = useState(false);
  const [dlFilter, setDlFilter] = useState("all"); // all | agent_wise | booking_wise
  const [dlAgentName, setDlAgentName] = useState("");
  const [dlBookingRef, setDlBookingRef] = useState("");
  const [dlDateFrom, setDlDateFrom] = useState(() => {
    const n = new Date();
    const yr = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1;
    return `${yr}-04-01`;
  });
  const [dlDateTo, setDlDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [dlBookingAgent, setDlBookingAgent] = useState(""); // 2-step booking filter: pick agent first
  const [agentSearch, setAgentSearch] = useState(""); // search bar on chips row
  // ── Vendor download dialog ───────────────────────────────────────
  const [showVendorDlDialog, setShowVendorDlDialog] = useState(false);
  const [vDlFilter, setVDlFilter] = useState("all"); // all | vendor_wise | booking_wise
  const [vDlVendorName, setVDlVendorName] = useState("");
  const [vDlBookingAgent, setVDlBookingAgent] = useState("");
  const [vDlBookingRef, setVDlBookingRef] = useState("");
  const [vDlDateFrom, setVDlDateFrom] = useState(() => {
    const n = new Date();
    const yr = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1;
    return `${yr}-04-01`;
  });
  const [vDlDateTo, setVDlDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  
  const [agentFormData, setAgentFormData] = useState({
    booking_id: "",
    payment_date: new Date().toISOString().split('T')[0],
    amount_received: "",
    bank_name: "",
    payment_mode: "",
    transaction_id: "",
    notes: ""
  });

  const [vendorFormData, setVendorFormData] = useState({
    booking_id: "",
    vendor_name: "",
    vendor_type: "",
    city_name: "",
    total_payable: "",
    payment_date: new Date().toISOString().split('T')[0],
    amount_paid: "",
    payment_mode: "",
    bank_name: "",
    transaction_id: "",
    notes: ""
  });

  const [selectedBooking, setSelectedBooking] = useState(null);
  // Existing vendor threads for the currently-selected booking (for Vendor Payment dropdown)
  const [bookingVendorThreads, setBookingVendorThreads] = useState([]);
  // "new" === free-text vendor; otherwise a vendor name from dropdown
  const [vendorChoice, setVendorChoice] = useState("new");

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [bookingsRes, listRes] = await Promise.all([
        axios.get(`${API}/bookings-for-payment`, { headers: { Authorization: `Bearer ${token}` } }),
        activeTab === "agent"
          ? axios.get(`${API}/payments`, { headers: { Authorization: `Bearer ${token}` } })
          : axios.get(`${API}/vendor-payments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBookings(bookingsRes.data || []);
      if (activeTab === "agent") {
        setPayments(listRes.data || []);
      } else {
        setVendorPayments(listRes.data || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Agent Payment Handlers ---

  const handleAgentBookingSelect = (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    setSelectedBooking(booking);
    setAgentFormData(prev => ({
      ...prev,
      booking_id: bookingId,
      amount_received: booking ? (booking.total_amount - (booking.amount_paid || 0)).toString() : ""
    }));
  };

  const handleVendorBookingSelect = async (bookingId) => {
    const booking = bookings.find(b => b.id === bookingId);
    setSelectedBooking(booking);
    setVendorFormData(prev => ({
      ...prev,
      booking_id: bookingId,
      vendor_name: "",
      vendor_type: "",
      city_name: "",
      total_payable: "",
      amount_paid: "",
    }));
    setVendorChoice("new");
    // Fetch existing vendor threads for this booking
    try {
      const res = await axios.get(`${API}/vendor-payments/by-booking/${bookingId}`, { headers: { Authorization: `Bearer ${token}` } });
      setBookingVendorThreads(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setBookingVendorThreads([]);
    }
  };

  const handleVendorChoice = (choice) => {
    setVendorChoice(choice);
    if (choice === "new") {
      setVendorFormData(prev => ({ ...prev, vendor_name: "", vendor_type: "", city_name: "", total_payable: "", amount_paid: "" }));
    } else {
      // Pre-fill from the existing thread
      const thread = bookingVendorThreads.find(v => v.vendor_name === choice);
      if (thread) {
        setVendorFormData(prev => ({
          ...prev,
          vendor_name: thread.vendor_name,
          vendor_type: thread.vendor_type || "",
          city_name: thread.city_name || "",
          total_payable: String(thread.total_payable || ""),
          amount_paid: "",
        }));
      }
    }
  };

  const currentVendorThread = (() => {
    if (!vendorChoice || vendorChoice === "new") return null;
    return bookingVendorThreads.find(v => v.vendor_name === vendorChoice) || null;
  })();

  const submitAgentPayment = async (e) => {
    e.preventDefault();
    if (savingAgentPayment) return;
    if (!agentFormData.booking_id || !agentFormData.amount_received || !agentFormData.payment_mode) {
      toast.error("Please fill required fields");
      return;
    }

    setSavingAgentPayment(true);
    try {
      const payload = { ...agentFormData, amount_received: parseFloat(agentFormData.amount_received) };

      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Payment updated!");
      } else {
        await axios.post(`${API}/payments`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Payment recorded!");
      }
      setShowAgentForm(false);
      setEditingPayment(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save payment");
    } finally {
      setSavingAgentPayment(false);
    }
  };

  const deleteAgentPayment = async (id) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await axios.delete(`${API}/payments/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Payment deleted!");
      fetchData();
    } catch (error) {
        toast.error("Failed to delete payment");
    }
  };

  // --- Vendor Payment Handlers ---

  const submitVendorPayment = async (e) => {
    e.preventDefault();
    if (savingVendorPayment) return;
    if (!vendorFormData.booking_id || !vendorFormData.vendor_name || !vendorFormData.total_payable || !vendorFormData.amount_paid || !vendorFormData.payment_mode) {
      toast.error("Please fill required fields");
      return;
    }

    setSavingVendorPayment(true);
    try {
      const payload = {
        ...vendorFormData,
        total_payable: parseFloat(vendorFormData.total_payable),
        amount_paid: parseFloat(vendorFormData.amount_paid)
      };

      if (editingVendorPayment) {
        await axios.put(`${API}/vendor-payments/${editingVendorPayment.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Vendor payment updated!");
      } else {
        await axios.post(`${API}/vendor-payments`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Vendor payment recorded!");
      }
      setShowVendorForm(false);
      setEditingVendorPayment(null);
      setVendorFormData({
        booking_id: "",
        vendor_name: "",
        vendor_type: "",
        city_name: "",
        total_payable: "",
        payment_date: new Date().toISOString().split('T')[0],
        amount_paid: "",
        payment_mode: "",
        bank_name: "",
        transaction_id: "",
        notes: ""
      });
      await fetchData();
    } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to save vendor payment");
    } finally {
      setSavingVendorPayment(false);
    }
  };

  const deleteVendorPayment = async (id) => {
    if (!window.confirm("Delete this vendor payment?")) return;
    try {
      await axios.delete(`${API}/vendor-payments/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Vendor payment deleted!");
      fetchData();
    } catch (error) {
        toast.error("Failed to delete vendor payment");
    }
  };

  const getModeLabel = (mode) => {
    const found = paymentModes.find(m => m.value === mode);
    return found ? found.label : mode;
  };

  // --- Cancelled-with-zero filter (request: hide cancelled bookings that have ₹0 received) ---
  const cancelledIdsAll = new Set(bookings.filter(b => (b.status || '').toLowerCase() === 'cancelled').map(b => b.id));
  const totalReceivedByBooking = payments.reduce((acc, p) => {
    if (!p.booking_id) return acc;
    acc[p.booking_id] = (acc[p.booking_id] || 0) + Number(p.amount_received || 0);
    return acc;
  }, {});
  const visiblePayments = payments.filter(p => {
    if (!cancelledIdsAll.has(p.booking_id)) return true;
    return (totalReceivedByBooking[p.booking_id] || 0) > 0; // keep cancelled rows only if money was actually received
  });

  // --- Calculations ---

  const totalBookingAmount = bookings.reduce((sum, b) => sum + (b.total_amount || 0), 0);
  const totalReceived = bookings.reduce((sum, b) => sum + (b.amount_paid || 0), 0);
  const totalBalance = Math.max(0, totalBookingAmount - totalReceived);
  // Calculate unique bookings that have payments
  const uniquePaidBookings = new Set(visiblePayments.map(p => p.booking_id)).size;

  // Active bookings for payment dropdowns: HIDE bookings that are BOTH fully paid AND completed.
  // (Cancelled bookings are also hidden — payment records aren't expected on them.)
  const todayStr = new Date().toISOString().slice(0, 10);
  const activeBookingsForPayment = bookings.filter((b) => {
    if ((b.status || "").toLowerCase() === "cancelled") return false;
    const totalAmt = Number(b.total_amount) || 0;
    const paidAmt = Number(b.amount_paid) || 0;
    const fullyPaid = totalAmt > 0 && paidAmt >= totalAmt - 0.01;
    const rd = (b.return_date || "").slice(0, 10);
    const isCompleted = rd && rd < todayStr;
    // Remove only when BOTH conditions hold; everything else stays selectable.
    return !(fullyPaid && isCompleted);
  });

  // Vendor totals — bug fix:
  // Each (booking_id, vendor_name) pair can have multiple installment rows,
  // all carrying the SAME `total_payable`. Summing it across rows triples/quadruples
  // the actual owed amount. We count `total_payable` once per pair (max across rows
  // to handle any post-edit adjustments) and sum `amount_paid` normally.
  const _vendorPayableByPair = {};
  vendorPayments.forEach((p) => {
    const key = `${p.booking_id || ''}::${(p.vendor_name || '').trim().toLowerCase()}`;
    const tp = Number(p.total_payable) || 0;
    _vendorPayableByPair[key] = Math.max(_vendorPayableByPair[key] || 0, tp);
  });
  const totalVendorPayable = Object.values(_vendorPayableByPair).reduce((s, v) => s + v, 0);
  const totalVendorPaid = vendorPayments.reduce((sum, p) => sum + (p.amount_paid || 0), 0);
  const totalVendorBalance = totalVendorPayable - totalVendorPaid;

  const filteredVendorPayments = vendorSearch.trim()
    ? vendorPayments.filter(p => (p.vendor_name || "").toLowerCase().includes(vendorSearch.trim().toLowerCase()))
    : vendorPayments;

  // ── Download helpers ─────────────────────────────────────────────
  const getAgentDownloadData = () => {
    let data = [...payments];
    if (dlFilter === "agent_wise" && dlAgentName) {
      data = data.filter(p => (p.agent_company || p.agent_name || '').trim() === dlAgentName);
    } else if (dlFilter === "booking_wise" && dlBookingRef) {
      data = data.filter(p => p.booking_ref === dlBookingRef);
    }
    if (dlDateFrom) data = data.filter(p => p.payment_date && p.payment_date >= dlDateFrom);
    if (dlDateTo) data = data.filter(p => p.payment_date && p.payment_date <= dlDateTo);
    return data;
  };

  const downloadAgentExcel = (dataOverride) => {
    const data = dataOverride !== undefined ? dataOverride : payments;
    if (!data.length) { toast.error("No records match the selected filters"); return; }
    const rows = [["Booking ID", "Payment Date", "Agent Company", "Agent Name", "Guest Name", "No of Pax", "Destination", "No of Nights", "Travel Date", "Return Date", "Booking Amt", "Received Amt", "Balance Amt", "Bank Name", "Mode", "Transaction Number"]];
    const totalByB = {}, paidByB = {};
    data.forEach(p => {
      const k = p.booking_id; if (!k) return;
      if (!(k in totalByB)) totalByB[k] = Number(p.total_booking_amount || 0);
      paidByB[k] = (paidByB[k] || 0) + Number(p.amount_received || 0);
    });
    data.forEach(p => {
      const bkTotal = totalByB[p.booking_id] || 0; const bkPaid = paidByB[p.booking_id] || 0;
      const paxStr = (p.pax_children||0) > 0 ? `${p.pax_adults||0} Adult${(p.pax_adults||0)===1?'':'s'} + ${p.pax_children} Child${p.pax_children===1?'':'ren'}` : `${p.pax_adults||0} Adult${(p.pax_adults||0)===1?'':'s'}`;
      rows.push([p.booking_ref||'', formatDate(p.payment_date), p.agent_company||'', p.agent_name||'', p.client_name||'', paxStr, p.destination||'', p.nights||'', formatDate(p.travel_date), formatDate(p.return_date), bkTotal, p.amount||p.amount_received||0, Math.max(0,bkTotal-bkPaid), p.bank_name||'', getModeLabel(p.payment_mode), p.transaction_id||'']);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "Agent_Payments.csv"; a.click();
    URL.revokeObjectURL(url); toast.success("Agent Payments Excel downloaded");
  };

  const downloadAgentPDF = (dataOverride) => {
    const data = dataOverride !== undefined ? dataOverride : payments;
    if (!data.length) return toast.error("No records match the selected filters");
    const win = window.open('', '_blank');
    const totalByB = {}, paidByB = {};
    data.forEach(p => {
      const k = p.booking_id; if (!k) return;
      if (!(k in totalByB)) totalByB[k] = Number(p.total_booking_amount || 0);
      paidByB[k] = (paidByB[k] || 0) + Number(p.amount_received || 0);
    });
    const rows = data.map(p => {
      const bkTotal = totalByB[p.booking_id] || 0; const bkPaid = paidByB[p.booking_id] || 0;
      const paxStr = (p.pax_children||0) > 0 ? `${p.pax_adults||0} Adult${(p.pax_adults||0)===1?'':'s'} + ${p.pax_children} Child${p.pax_children===1?'':'ren'}` : `${p.pax_adults||0} Adult${(p.pax_adults||0)===1?'':'s'}`;
      return `<tr><td>#${p.booking_ref||''}</td><td>${formatDate(p.payment_date)}</td><td>${p.client_name||'-'}<br/><small>${paxStr}</small></td><td>${p.destination||'-'}<br/><small>${p.nights?p.nights+'N':''}</small></td><td>${formatDate(p.travel_date)}<br/><small>${p.return_date?formatDate(p.return_date):''}</small></td><td>₹${bkTotal.toLocaleString()}</td><td style="color:#16a34a">₹${(p.amount||p.amount_received||0).toLocaleString()}</td><td style="color:${Math.max(0,bkTotal-bkPaid)>0?'#dc2626':'#16a34a'}">₹${Math.max(0,bkTotal-bkPaid).toLocaleString()}</td><td>${p.bank_name||'-'}</td><td>${getModeLabel(p.payment_mode)}</td><td>${p.transaction_id||'-'}</td></tr>`;
    }).join('');
    win.document.write(`<html><head><title>Agent Payments</title><style>body{font-family:sans-serif;padding:20px;font-size:11px}h2{color:#059669}table{width:100%;border-collapse:collapse}th{background:#f0fdf4;padding:8px 6px;text-align:left;border-bottom:2px solid #059669;font-size:10px;text-transform:uppercase}td{padding:7px 6px;border-bottom:1px solid #e5e7eb;vertical-align:top}</style></head><body><h2>Agent Payments Report</h2><p>Generated: ${new Date().toLocaleDateString('en-IN')}</p><table><thead><tr><th>Booking ID</th><th>Pymt Date</th><th>Guest / Pax</th><th>Dest / Nights</th><th>Travel Date</th><th>Booking Amt</th><th>Received</th><th>Balance</th><th>Bank</th><th>Mode</th><th>Txn No.</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    win.document.close(); win.print(); toast.success("Agent Payments PDF ready to print");
  };

  const getVendorDownloadData = () => {
    let data = [...vendorPayments];
    if (vDlFilter === "vendor_wise" && vDlVendorName) {
      data = data.filter(p => (p.vendor_name || '').trim() === vDlVendorName);
    } else if (vDlFilter === "booking_wise" && vDlBookingRef) {
      data = data.filter(p => p.booking_ref === vDlBookingRef);
    }
    if (vDlDateFrom) data = data.filter(p => p.payment_date && p.payment_date >= vDlDateFrom);
    if (vDlDateTo) data = data.filter(p => p.payment_date && p.payment_date <= vDlDateTo);
    return data;
  };

  const downloadVendorExcel = (dataOverride) => {
    const data = dataOverride !== undefined ? dataOverride : vendorPayments;
    if (!data.length) { toast.error("No records match the selected filters"); return; }
    const rows = [["Booking ID", "Payment Date", "Vendor Type", "City Name", "Vendor Name", "Agent Company", "Agent Name", "Guest Name", "Destination", "Nights", "Payable Amt", "Paid Amt", "Balance Amt", "Bank Name", "Mode", "Transaction Number"]];
    data.forEach(p => {
      const balance = Math.max(0,(p.total_payable||0)-(p.amount_paid||0));
      rows.push([p.booking_ref||'', formatDate(p.payment_date), p.vendor_type||'', p.city_name||'', p.vendor_name||'', p.agent_company||'', p.agent_name||'', p.client_name||'', p.destination||'', p.nights||'', p.total_payable||0, p.amount_paid||0, balance, p.bank_name||'', getModeLabel(p.payment_mode), p.transaction_id||'']);
    });
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "Vendor_Payments.csv"; a.click();
    URL.revokeObjectURL(url); toast.success("Vendor Payments Excel downloaded");
  };

  const downloadVendorPDF = (dataOverride) => {
    const data = dataOverride !== undefined ? dataOverride : vendorPayments;
    if (!data.length) return toast.error("No records match the selected filters");
    const win = window.open('', '_blank');
    const rows = data.map(p => {
      const balance = Math.max(0,(p.total_payable||0)-(p.amount_paid||0));
      return `<tr><td>#${p.booking_ref||''}</td><td>${formatDate(p.payment_date)}</td><td>${p.vendor_type||'-'}</td><td>${p.city_name||'-'}</td><td>${p.vendor_name||'-'}</td><td>₹${(p.total_payable||0).toLocaleString()}</td><td style="color:#16a34a">₹${(p.amount_paid||0).toLocaleString()}</td><td style="color:${balance>0?'#dc2626':'#16a34a'}">₹${balance.toLocaleString()}</td><td>${p.bank_name||'-'}</td><td>${getModeLabel(p.payment_mode)}</td><td>${p.transaction_id||'-'}</td></tr>`;
    }).join('');
    win.document.write(`<html><head><title>Vendor Payments</title><style>body{font-family:sans-serif;padding:20px;font-size:11px}h2{color:#d97706}table{width:100%;border-collapse:collapse}th{background:#fffbeb;padding:8px 6px;text-align:left;border-bottom:2px solid #d97706;font-size:10px;text-transform:uppercase}td{padding:7px 6px;border-bottom:1px solid #e5e7eb;vertical-align:top}</style></head><body><h2>Vendor Payments Report</h2><p>Generated: ${new Date().toLocaleDateString('en-IN')}</p><table><thead><tr><th>Booking ID</th><th>Pymt Date</th><th>Type</th><th>City</th><th>Vendor</th><th>Payable</th><th>Paid</th><th>Balance</th><th>Bank</th><th>Mode</th><th>Txn No.</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
    win.document.close(); win.print(); toast.success("Vendor Payments PDF ready to print");
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-payments">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Payments & Accounts</h1>
            <p className="text-gray-500">Manage agent receivables and vendor payables</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-50">
            <TabsTrigger value="agent" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
              <User className="w-4 h-4 mr-2" /> Agent Payments (In)
            </TabsTrigger>
            <TabsTrigger value="vendor" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
              <Truck className="w-4 h-4 mr-2" /> Vendor Payments (Out)
            </TabsTrigger>
          </TabsList>

          {/* AGENT PAYMENTS TAB */}
          <TabsContent value="agent" className="space-y-6">
            <div className="flex flex-wrap items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowAgentDlDialog(true)} disabled={!payments.length} className="gap-2 rounded-xl" data-testid="agent-download-report">
                  <Download className="w-4 h-4" /> Download Report
                </Button>
                <Button onClick={() => { setEditingPayment(null); setShowAgentForm(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                    <Plus className="w-4 h-4" /> Record Agent Payment
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                    <p className="text-gray-500 text-sm">Total Bookings</p>
                    <p className="text-2xl font-bold text-blue-400">{uniquePaidBookings}</p>
                    </CardContent>
                </Card>
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                    <p className="text-gray-500 text-sm">Total Booking Amount</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalBookingAmount)}</p>
                    </CardContent>
                </Card>
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                    <p className="text-gray-500 text-sm">Total Received Amount</p>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(totalReceived)}</p>
                    </CardContent>
                </Card>
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                    <p className="text-gray-500 text-sm">Total Balance Amount</p>
                    <p className="text-2xl font-bold text-red-400">{formatCurrency(totalBalance)}</p>
                    </CardContent>
                </Card>
            </div>

            {/* Table */}
            <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-0">
                    {/* Filter chips */}
                    {(() => {
                      // Build per-booking total/paid/status maps once for the chips
                      const totalByB = {}, paidByB = {};
                      visiblePayments.forEach(p => {
                        const k = p.booking_id; if (!k) return;
                        if (!(k in totalByB)) totalByB[k] = Number(p.total_booking_amount || 0);
                        paidByB[k] = (paidByB[k] || 0) + Number(p.amount_received || 0);
                      });
                      const cancelledIds = new Set(bookings.filter(b => (b.status || '').toLowerCase() === 'cancelled').map(b => b.id));
                      const statusFor = (bid) => {
                        const t = totalByB[bid] || 0; const pd = paidByB[bid] || 0;
                        if (t <= 0 || pd <= 0) return 'unpaid';
                        if (pd >= t) return 'paid';
                        return 'partial';
                      };
                      const counts = {
                        all: visiblePayments.length,
                        unpaid: visiblePayments.filter(p => !cancelledIds.has(p.booking_id) && statusFor(p.booking_id) === 'unpaid').length,
                        partial: visiblePayments.filter(p => !cancelledIds.has(p.booking_id) && statusFor(p.booking_id) === 'partial').length,
                        paid: visiblePayments.filter(p => !cancelledIds.has(p.booking_id) && statusFor(p.booking_id) === 'paid').length,
                      };
                      return (
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 gap-3 flex-wrap" data-testid="admin-agent-payments-chips">
                          <div className="flex items-center gap-2 flex-wrap">
                          {[
                            { key: 'all', label: 'All', color: 'bg-gray-800 text-white', inactive: 'bg-gray-100 text-gray-700' },
                            { key: 'unpaid', label: 'Unpaid', color: 'bg-rose-600 text-white', inactive: 'bg-rose-50 text-rose-700' },
                            { key: 'partial', label: 'Partial', color: 'bg-amber-500 text-white', inactive: 'bg-amber-50 text-amber-700' },
                            { key: 'paid', label: 'Paid', color: 'bg-emerald-600 text-white', inactive: 'bg-emerald-50 text-emerald-700' },
                          ].map(c => {
                            const active = agentPaymentFilter === c.key;
                            return (
                              <button key={c.key} onClick={() => setAgentPaymentFilter(c.key)}
                                data-testid={`admin-agent-payments-filter-${c.key}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active ? c.color + ' shadow-sm' : c.inactive + ' hover:brightness-95'}`}>
                                {c.label} <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] ${active ? 'bg-white/20' : 'bg-white/70'}`}>{counts[c.key]}</span>
                              </button>
                            );
                          })}
                          </div>
                          {/* Search bar — right side */}
                          <div className="relative">
                            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <Input
                              value={agentSearch}
                              onChange={e => setAgentSearch(e.target.value)}
                              placeholder="Search agent, guest, booking..."
                              className="pl-9 bg-gray-50 border-gray-200 h-8 text-sm w-64"
                              data-testid="agent-payment-search"
                            />
                          </div>
                        </div>
                      );
                    })()}
                    {visiblePayments.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">No agent payments recorded</div>
                    ) : (
                        (() => {
                          // ── Build booking-level map ─────────────────────────────────
                          const balanceById = {};
                          const totalByB = {}, paidByB = {};
                          const grouped = visiblePayments.reduce((acc, p) => {
                            const key = p.booking_id || p.id;
                            (acc[key] = acc[key] || []).push(p);
                            return acc;
                          }, {});
                          Object.entries(grouped).forEach(([key, list]) => {
                            const sorted = [...list].sort((a, b) => new Date(a.payment_date||0) - new Date(b.payment_date||0));
                            const total = Number((sorted[0] || {}).total_booking_amount || 0);
                            totalByB[key] = total; let running = 0;
                            sorted.forEach(p => { running += Number(p.amount_received || 0); balanceById[p.id] = Math.max(0, total - running); });
                            paidByB[key] = running;
                          });
                          const cancelledIds = new Set(bookings.filter(b => (b.status||'').toLowerCase() === 'cancelled').map(b => b.id));
                          const statusFor = bid => {
                            const t = totalByB[bid]||0, pd = paidByB[bid]||0;
                            if (t<=0||pd<=0) return 'unpaid'; if (pd>=t) return 'paid'; return 'partial';
                          };
                          const visible = agentPaymentFilter === 'all'
                            ? visiblePayments
                            : visiblePayments.filter(p => !cancelledIds.has(p.booking_id) && statusFor(p.booking_id) === agentPaymentFilter);

                          // ── Apply search filter ─────────────────────────────────────
                          const searched = agentSearch.trim()
                            ? visible.filter(p => {
                                const q = agentSearch.toLowerCase();
                                return (p.agent_company||'').toLowerCase().includes(q)
                                  || (p.agent_name||'').toLowerCase().includes(q)
                                  || (p.client_name||'').toLowerCase().includes(q)
                                  || (p.booking_ref||'').toLowerCase().includes(q)
                                  || (p.destination||'').toLowerCase().includes(q);
                              })
                            : visible;

                          // ── Group by agent ─────────────────────────────────────────
                          const agentMap = {};
                          searched.forEach(p => {
                            const aKey = (p.agent_company || p.agent_name || 'Unknown Agent').trim();
                            if (!agentMap[aKey]) agentMap[aKey] = { agent_name: p.agent_name, agent_company: p.agent_company, logo_url: p.logo_url || '', agent_phone: p.agent_phone || '-', payments: [] };
                            agentMap[aKey].payments.push(p);
                          });
                          const agentGroups = Object.entries(agentMap).map(([key, ag]) => {
                            // collapse to per-booking rows
                            const bkMap = {};
                            ag.payments.forEach(p => {
                              const bid = p.booking_id || p.id;
                            if (!bkMap[bid]) bkMap[bid] = { booking_id: bid, booking_ref: p.booking_ref, booking_date: p.booking_date||'', client_name: p.client_name, destination: p.destination, travel_date: p.travel_date, return_date: p.return_date, pax_adults: p.pax_adults||0, pax_children: p.pax_children||0, nights: p.nights||0, total_amount: totalByB[bid]||0, paid: paidByB[bid]||0, trip_type: p.trip_type||'domestic', payments: [] };
                              bkMap[bid].payments.push(p);
                            });
                            const bkRows = Object.values(bkMap);
                            const totalBA = bkRows.reduce((s,b) => s+(b.total_amount||0), 0);
                            const totalPaid = bkRows.reduce((s,b) => s+(b.paid||0), 0);
                            const totalPax = bkRows.reduce((s,b) => s+b.pax_adults+b.pax_children, 0);
                            return { key, ...ag, bkRows, totalBA, totalPaid, totalBal: Math.max(0, totalBA-totalPaid), totalPax };
                          });
                          agentGroups.sort((a,b) => b.totalBA - a.totalBA);

                          if (agentGroups.length === 0) return <div className="px-4 py-8 text-center text-gray-400">{agentSearch.trim() ? `No results for "${agentSearch}"` : 'No payments match this filter.'}</div>;

                          return (
                            <div className="divide-y divide-gray-100">
                              {agentGroups.map(ag => {
                                const isOpen = expandedAgentKey === ag.key;
                                const initials = (ag.agent_company || ag.agent_name || 'A').trim().charAt(0).toUpperCase();
                                // Compute completed / domestic / intl for this agent
                                const todayStr = new Date().toISOString().slice(0, 10);
                                const completedCount = ag.bkRows.filter(b => b.return_date && b.return_date.slice(0,10) < todayStr).length;
                                const domesticCount = ag.payments.filter(p => (p.trip_type || 'domestic').toLowerCase() === 'domestic')
                                  .reduce((s, p) => s.has(p.booking_id) ? s : s.add(p.booking_id), new Set()).size;
                                const intlCount = ag.bkRows.length - domesticCount;
                                return (
                                  <div key={ag.key} data-testid={`agent-accordion-${ag.key}`}>
                                    {/* ── Agent Header Row ──────────────────────────── */}
                                    <div
                                      className="grid items-center px-6 py-4 bg-gray-50 hover:bg-emerald-50/40 cursor-pointer transition-colors"
                                      style={AGENT_GRID}
                                      onClick={() => { setExpandedAgentKey(isOpen ? null : ag.key); setExpandedBookingKeys(new Set()); }}
                                    >
                                      {/* 1 – Agent info (tracks 1-2) */}
                                      <div className="flex items-center gap-3 min-w-0" style={{gridColumn:'1 / 3'}}>
                                        {ag.logo_url
                                          ? <img src={ag.logo_url} alt={ag.agent_company || ag.agent_name || ''} className="w-10 h-10 rounded-full object-cover bg-white border border-gray-200 shrink-0" />
                                          : <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm shrink-0">{initials}</div>}
                                        <div className="min-w-0">
                                          <p className="font-semibold text-gray-900 text-sm truncate">{ag.agent_company || ag.agent_name || 'Unknown'}</p>
                                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                                            {ag.agent_name && ag.agent_name !== ag.agent_company && <span className="text-xs text-gray-500">{ag.agent_name}</span>}
                                            {ag.agent_phone && ag.agent_phone !== '-' && <span className="flex items-center gap-1 text-xs text-gray-400"><Phone className="w-3 h-3"/>{ag.agent_phone}</span>}
                                          </div>
                                        </div>
                                      </div>
                                      {/* 2-6 – Stats (tracks 3-5, no counterpart in booking rows) */}
                                      <div className="flex items-center justify-around gap-2 min-w-0" style={{gridColumn:'3 / 6'}}>
                                      {/* 2 – Bookings */}
                                      <div className="text-center"><p className="text-[10px] text-gray-400 uppercase tracking-wide">Bookings</p><p className="font-bold text-gray-800 text-sm">{ag.bkRows.length}</p></div>
                                      {/* 3 – Completed */}
                                      <div className="text-center"><p className="text-[10px] text-gray-400 uppercase tracking-wide">Completed</p><p className="font-bold text-emerald-600 text-sm">{completedCount}</p></div>
                                      {/* 4 – Pax */}
                                      <div className="text-center"><p className="text-[10px] text-gray-400 uppercase tracking-wide">Pax</p><p className="font-bold text-gray-800 text-sm">{ag.totalPax}</p></div>
                                      {/* 5 – Domestic */}
                                      <div className="text-center"><p className="text-[10px] text-gray-400 uppercase tracking-wide">Domestic</p><p className="font-bold text-blue-600 text-sm">{domesticCount}</p></div>
                                      {/* 6 – Intl */}
                                      <div className="text-center"><p className="text-[10px] text-gray-400 uppercase tracking-wide">Intl</p><p className="font-bold text-indigo-600 text-sm">{intlCount}</p></div>
                                      </div>
                                      {/* 7 – Booking Amt */}
                                      <div className="text-right" style={{gridColumn:6}}><p className="text-[10px] text-gray-400 uppercase tracking-wide">Booking Amt</p><p className="font-bold text-gray-900 text-sm">{formatCurrency(ag.totalBA)}</p></div>
                                      {/* 8 – Received */}
                                      <div className="text-right" style={{gridColumn:7}}><p className="text-[10px] text-gray-400 uppercase tracking-wide">Received</p><p className="font-bold text-emerald-600 text-sm">{formatCurrency(ag.totalPaid)}</p></div>
                                      {/* 9 – Balance */}
                                      <div className="text-right" style={{gridColumn:8}}><p className="text-[10px] text-gray-400 uppercase tracking-wide">Balance</p><p className={`font-bold text-sm ${ag.totalBal > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{ag.totalBal > 0 ? formatCurrency(ag.totalBal) : 'Nil'}</p></div>
                                      {/* 10 – Chevron (track 11) */}
                                      <div className="flex justify-end" style={{gridColumn:11}}>{isOpen ? <ChevronUp className="w-4 h-4 text-gray-400"/> : <ChevronDown className="w-4 h-4 text-gray-400"/>}</div>
                                    </div>

                                    {/* ── Level 2: Booking ID rows ──────────────────── */}
                                    {isOpen && (
                                      <div className="bg-white divide-y divide-gray-50">
                                        {/* Column headers for booking rows */}
                                        <div
                                          className="grid items-center px-6 py-2 bg-gray-100/80"
                                          style={AGENT_GRID}
                                        >
                                          {['Booking ID','Booking Date','Guest','Destination','Travel Date','Booking Amt','Received Amt','Balance','Action','Pmts',''].map((h,i) => (
                                            <div key={i} className={`text-[10px] font-semibold text-gray-500 uppercase tracking-wide truncate ${i>=5&&i<=7?'text-right':i===8||i===9?'text-center':''}`}>{h}</div>
                                          ))}
                                        </div>
                                        {ag.bkRows.map(bk => {
                                          const bkKey = `${ag.key}::${bk.booking_id}`;
                                          const isBkOpen = expandedBookingKeys.has(bkKey);
                                          const bkBal = Math.max(0, bk.total_amount - bk.paid);
                                          const bkPaxStr = (bk.pax_children||0) > 0
                                            ? `${bk.pax_adults||0}A + ${bk.pax_children}C`
                                            : `${bk.pax_adults||0} Adults`;
                                          return (
                                            <div key={bkKey}>
                                              {/* Booking summary row */}
                                              <div
                                                className="grid items-center px-6 py-2.5 hover:bg-emerald-50/20 transition-colors"
                                                style={AGENT_GRID}
                                                data-testid={`booking-accordion-${bk.booking_id}`}
                                              >
                                                {/* Booking ID */}
                                                <div><span className="font-mono text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">#{bk.booking_ref || bk.booking_id?.slice(-8).toUpperCase()}</span></div>
                                                {/* Booking Date */}
                                                <div><p className="text-gray-600 text-xs">{bk.booking_date ? formatDate(bk.booking_date) : '-'}</p></div>
                                                {/* Guest */}
                                                <div>
                                                  <p className="text-gray-800 font-medium text-xs truncate">{bk.client_name || '-'}</p>
                                                  <p className="text-gray-400 text-[11px]">{bkPaxStr}</p>
                                                </div>
                                                {/* Destination + Nights */}
                                                <div>
                                                  <p className="text-gray-700 text-xs font-medium truncate">{bk.destination || '-'}</p>
                                                  {(bk.nights||0) > 0 && <p className="text-gray-400 text-[11px]">{bk.nights} Nights</p>}
                                                </div>
                                                {/* Travel Date */}
                                                <div className="text-xs">
                                                  <p className="text-gray-700">{bk.travel_date ? formatDate(bk.travel_date) : '-'}</p>
                                                  {bk.return_date && <p className="text-gray-400 text-[11px]">→ {formatDate(bk.return_date)}</p>}
                                                </div>
                                                {/* Booking Amt */}
                                                <div className="text-right"><p className="text-gray-800 font-semibold text-xs">{formatCurrency(bk.total_amount)}</p></div>
                                                {/* Received */}
                                                <div className="text-right"><p className="text-emerald-600 font-semibold text-xs">{formatCurrency(bk.paid)}</p></div>
                                                {/* Balance */}
                                                <div className="text-right"><p className={`font-semibold text-xs ${bkBal > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{bkBal > 0 ? formatCurrency(bkBal) : 'Nil'}</p></div>
                                                {/* Action: View */}
                                                <div className="text-center">
                                                  <Link to={`/admin/bookings/${bk.booking_id}`} onClick={e => e.stopPropagation()}>
                                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-emerald-600 gap-0.5 text-xs hover:bg-emerald-50">
                                                      <ExternalLink className="w-3 h-3"/>View
                                                    </Button>
                                                  </Link>
                                                </div>
                                                {/* No of Payments */}
                                                <div className="text-center">
                                                  <button
                                                    className="text-[11px] text-gray-500 bg-gray-100 hover:bg-emerald-50 px-1.5 py-0.5 rounded transition-colors"
                                                    onClick={e => { e.stopPropagation(); const next = new Set(expandedBookingKeys); isBkOpen ? next.delete(bkKey) : next.add(bkKey); setExpandedBookingKeys(next); }}
                                                  >
                                                    {bk.payments.length} txn
                                                  </button>
                                                </div>
                                                {/* Chevron */}
                                                <div
                                                  className="flex justify-end cursor-pointer"
                                                  onClick={e => { e.stopPropagation(); const next = new Set(expandedBookingKeys); isBkOpen ? next.delete(bkKey) : next.add(bkKey); setExpandedBookingKeys(next); }}
                                                >
                                                  {isBkOpen ? <ChevronUp className="w-3.5 h-3.5 text-gray-400"/> : <ChevronDown className="w-3.5 h-3.5 text-gray-300"/>}
                                                </div>
                                              </div>

                                              {/* ↳ Payment detail rows — indented under Booking ID */}
                                              {isBkOpen && (
                                                <div className="flex bg-gray-50/60 border-l-2 border-emerald-200 ml-6">
                                                  {/* L-arrow indicator column — aligns with Booking ID width */}
                                                  <div className="w-[110px] shrink-0 flex items-start justify-center pt-3 text-emerald-300 text-xl select-none">↳</div>
                                                  {/* Payment table — starts from Guest column onwards */}
                                                  <div className="flex-1 px-3 pb-3 pt-1.5 overflow-x-auto">
                                                    <table className="w-full text-xs border-separate border-spacing-0">
                                                      <thead>
                                                        <tr className="text-[10px] uppercase tracking-wide text-gray-400">
                                                          <th className="py-1.5 pr-3 text-left w-28">Payment Date</th>
                                                          <th className="py-1.5 pr-3 text-right w-24">Received Amt</th>
                                                          <th className="py-1.5 pr-3 text-center w-28">Bank Name</th>
                                                          <th className="py-1.5 pr-3 text-center w-24">Mode</th>
                                                          <th className="py-1.5 pr-3 text-center w-28">TXN Number</th>
                                                          <th className="py-1.5 pr-3 text-left w-32">Remarks</th>
                                                          <th className="py-1.5 pr-3 text-center w-28">Action</th>
                                                        </tr>
                                                      </thead>
                                                      <tbody>
                                                        {bk.payments.map(p => (
                                                          <tr key={p.id} className="hover:bg-white border-b border-gray-100">
                                                            <td className="py-1.5 pr-3 text-gray-600">{formatDate(p.payment_date)}</td>
                                                            <td className="py-1.5 pr-3 text-right text-emerald-600 font-semibold">{formatCurrency(p.amount_received)}</td>
                                                            <td className="py-1.5 pr-3 text-center text-gray-600">{p.bank_name || '-'}</td>
                                                            <td className="py-1.5 pr-3 text-center"><Badge variant="outline" className="text-[10px] px-1.5">{getModeLabel(p.payment_mode)}</Badge></td>
                                                            <td className="py-1.5 pr-3 text-center text-gray-500 font-mono text-[11px]">{p.transaction_id || '-'}</td>
                                                            <td className="py-1.5 pr-3 text-gray-500 text-[11px] max-w-[128px] truncate">{p.notes || '-'}</td>
                                                            <td className="py-1.5 pr-3 text-center">
                                                              <div className="flex items-center justify-center gap-1">
                                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditingPayment(p); setAgentFormData(p); setShowAgentForm(true); }}><Edit className="w-3 h-3 text-gray-500"/></Button>
                                                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => deleteAgentPayment(p.id)}><Trash2 className="w-3 h-3 text-red-400"/></Button>
                                                                <Link to={`/admin/bookings/${p.booking_id}`}><Button variant="ghost" size="sm" className="h-6 px-2 text-emerald-600 gap-0.5"><ExternalLink className="w-3 h-3"/>View</Button></Link>
                                                              </div>
                                                            </td>
                                                          </tr>
                                                        ))}
                                                      </tbody>
                                                    </table>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()
                    )}
                </CardContent>
            </Card>
          </TabsContent>

          {/* VENDOR PAYMENTS TAB */}
          <TabsContent value="vendor" className="space-y-6">
            <div className="flex flex-wrap items-center justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowVendorDlDialog(true)} disabled={!vendorPayments.length} className="gap-2 rounded-xl" data-testid="vendor-download-report">
                  <Download className="w-4 h-4" /> Download Report
                </Button>
                  <div className="relative w-72">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <Input
                        value={vendorSearch}
                        onChange={e => setVendorSearch(e.target.value)}
                        placeholder="Search by vendor name..."
                        className="pl-9 bg-gray-50 border-gray-200 h-9 text-sm w-full"
                        data-testid="vendor-payment-search"
                    />
                  </div>
                  <Button onClick={() => { setEditingVendorPayment(null); setShowVendorForm(true); }} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                      <Plus className="w-4 h-4" /> Record Vendor Payment
                  </Button>
            </div>

            {/* Vendor Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                    <p className="text-gray-500 text-sm">Total Vendor Payables</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalVendorPayable)}</p>
                    </CardContent>
                </Card>
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                    <p className="text-gray-500 text-sm">Total Paid to Vendors</p>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(totalVendorPaid)}</p>
                    </CardContent>
                </Card>
                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-5">
                    <p className="text-gray-500 text-sm">Vendor Balance Due</p>
                    <p className="text-2xl font-bold text-red-400">{formatCurrency(totalVendorBalance)}</p>
                    </CardContent>
                </Card>
            </div>

             {/* Vendor Accordion — grouped by Booking */}
             <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="p-0">
                    {vendorPayments.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">No vendor payments recorded</div>
                    ) : filteredVendorPayments.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">No vendor payments match "{vendorSearch}"</div>
                    ) : (
                        (() => {
                          // Group by booking_id
                          const bookingMap = {};
                          filteredVendorPayments.forEach(p => {
                            const bk = p.booking_id || 'unknown';
                            if (!bookingMap[bk]) bookingMap[bk] = {
                              booking_id: bk,
                              booking_number: p.booking_number || bk.slice(0,8).toUpperCase(),
                              booking_ref: p.booking_ref || bk.slice(0,8).toUpperCase(),
                              agent_company: p.agent_company || '-',
                              agent_name: p.agent_name || '-',
                              client_name: p.client_name || '-',
                              destination: p.destination || '-',
                              nights: p.nights || 0,
                              travel_date: p.travel_date || '',
                              return_date: p.return_date || '',
                              pax_adults: p.pax_adults || 0,
                              pax_children: p.pax_children || 0,
                              payments: []
                            };
                            bookingMap[bk].payments.push(p);
                          });
                          const bookingGroups = Object.values(bookingMap).map(bg => {
                            const vendorTotals = {};
                            bg.payments.forEach(p => {
                              const vk = (p.vendor_name||'').trim().toLowerCase();
                              vendorTotals[vk] = Math.max(vendorTotals[vk]||0, Number(p.total_payable||0));
                            });
                            const totalPayable = Object.values(vendorTotals).reduce((s,v)=>s+v, 0);
                            const totalPaid = bg.payments.reduce((s,p)=>s+(Number(p.amount_paid)||0), 0);
                            const latestDate = bg.payments.reduce((latest, p) => {
                              const d = p.payment_date || '';
                              return d > latest ? d : latest;
                            }, '');
                            return { ...bg, totalPayable, totalPaid, totalBal: Math.max(0, totalPayable - totalPaid), latestDate };
                          });
                          // Sort by latest payment date descending (most recent booking on top)
                          bookingGroups.sort((a, b) => (b.latestDate > a.latestDate ? 1 : -1));
                          const paxStr = (a, c) => Number(c) > 0 ? `${a} Adult${Number(a)===1?'':'s'} + ${c} Child${Number(c)===1?'':'ren'}` : `${a} Adult${Number(a)===1?'':'s'}`;
                          const fmtD = (s) => { if (!s) return '-'; const d = new Date(s); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; };

                          return (
                            <div className="divide-y divide-gray-100">
                              {bookingGroups.map(bg => {
                                const isOpen = expandedVendorKey === bg.booking_id;
                                return (
                                  <div key={bg.booking_id} data-testid={`vendor-accordion-${bg.booking_id}`}>
                                    {/* Booking Header Row */}
                                    <div
                                      className="flex items-center gap-4 px-5 py-4 bg-gray-50 hover:bg-amber-50/40 cursor-pointer transition-colors"
                                      onClick={() => setExpandedVendorKey(isOpen ? null : bg.booking_id)}
                                    >
                                      {/* 1 – Booking ID */}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">#{bg.booking_ref || bg.booking_number}</p>
                                      </div>
                                      {/* 2 – Agent Detail */}
                                      <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-900 text-sm truncate">{bg.agent_company}</p>
                                        {bg.agent_name && bg.agent_name !== bg.agent_company && (
                                          <p className="text-xs text-gray-500 truncate">{bg.agent_name}</p>
                                        )}
                                      </div>
                                      {/* 3 – Guest Detail */}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">{bg.client_name}</p>
                                        <p className="text-xs text-gray-400">{paxStr(bg.pax_adults, bg.pax_children)}</p>
                                      </div>
                                      {/* 4 – Destination */}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800 truncate">{bg.destination}</p>
                                        {bg.nights > 0 && <p className="text-xs text-gray-400">{bg.nights} Nights</p>}
                                      </div>
                                      {/* 5 – Travel Date */}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-800">{fmtD(bg.travel_date)}</p>
                                        {bg.return_date && <p className="text-xs text-gray-400">{fmtD(bg.return_date)}</p>}
                                      </div>
                                      {/* 6 – Vendor Amt */}
                                      <div className="flex-1 min-w-0 text-right"><p className="text-[10px] text-gray-400 uppercase tracking-wide">Vendor Amt</p><p className="font-bold text-gray-900 text-sm">{formatCurrency(bg.totalPayable)}</p></div>
                                      {/* 7 – Paid Amt */}
                                      <div className="flex-1 min-w-0 text-right"><p className="text-[10px] text-gray-400 uppercase tracking-wide">Paid Amt</p><p className="font-bold text-emerald-600 text-sm">{formatCurrency(bg.totalPaid)}</p></div>
                                      {/* 8 – Balance Amt */}
                                      <div className="flex-1 min-w-0 text-right"><p className="text-[10px] text-gray-400 uppercase tracking-wide">Balance Amt</p><p className={`font-bold text-sm ${bg.totalBal > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{bg.totalBal > 0 ? formatCurrency(bg.totalBal) : 'Nil'}</p></div>
                                      {/* 9 – Records toggle */}
                                      <div className="shrink-0">
                                        <div className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors ${isOpen ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-700'}`}>
                                          {isOpen ? <><ChevronUp className="w-3.5 h-3.5"/>Collapse</> : <><ChevronDown className="w-3.5 h-3.5"/>{bg.payments.length} Record{bg.payments.length !== 1 ? 's' : ''}</>}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Expanded: per-payment detail rows */}
                                    {isOpen && (
                                      <div className="bg-white px-5 pb-4 pt-4">
                                        <div className="overflow-x-auto">
                                          <table className="w-full text-sm table-fixed border-separate border-spacing-0">
                                            <thead>
                                              <tr className="text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                                                <th className="py-2.5 pr-3 text-left font-semibold w-24">Payment Date</th>
                                                <th className="py-2.5 pr-3 text-left font-semibold w-20">Type</th>
                                                <th className="py-2 pr-3 text-left font-semibold w-24">City Name</th>
                                                <th className="py-2 pr-3 text-left font-semibold w-28">Vendor Name</th>
                                                <th className="py-2 pr-3 text-center font-semibold w-24">Payable Amt</th>
                                                <th className="py-2 pr-3 text-center font-semibold w-24">Paid Amt</th>
                                                <th className="py-2 pr-3 text-center font-semibold w-24">Balance Amt</th>
                                                <th className="py-2 pr-3 text-center font-semibold w-28">Bank Name</th>
                                                <th className="py-2 pr-3 text-center font-semibold w-24">Mode</th>
                                                <th className="py-2 pr-3 text-center font-semibold w-24">Txn Number</th>
                                                <th className="py-2 text-center font-semibold w-16">Actions</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                              {bg.payments.map(p => {
                                                const balance = (p.balance_amount !== undefined && p.balance_amount !== null) ? Number(p.balance_amount) : Math.max(0,(p.total_payable||0)-(p.amount_paid||0));
                                                return (
                                                  <tr key={p.id} className="hover:bg-gray-50">
                                                    <td className="py-2.5 pr-3 text-gray-600 text-xs whitespace-nowrap">{formatDate(p.payment_date)}</td>
                                                    <td className="py-2.5 pr-3">
                                                      {p.vendor_type ? <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-indigo-50 text-indigo-700">{p.vendor_type}</span> : <span className="text-gray-300 text-xs">-</span>}
                                                    </td>
                                                    <td className="py-2.5 pr-3 text-gray-700 text-xs">{p.city_name || '-'}</td>
                                                    <td className="py-2.5 pr-3 text-gray-800 font-medium text-xs">{p.vendor_name || '-'}</td>
                                                    <td className="py-2.5 pr-3 text-center text-gray-600 text-xs">{formatCurrency(p.total_payable)}</td>
                                                    <td className="py-2.5 pr-3 text-center text-emerald-600 font-semibold text-xs">{formatCurrency(p.amount_paid)}</td>
                                                    <td className="py-2.5 pr-3 text-center font-semibold text-xs"><span className={balance > 0 ? 'text-red-600':'text-emerald-600'}>{balance > 0 ? formatCurrency(balance) : 'Nil'}</span></td>
                                                    <td className="py-2.5 pr-3 text-center text-gray-600 text-xs">{p.bank_name || '-'}</td>
                                                    <td className="py-2.5 pr-3 text-center"><Badge variant="outline" className="text-[10px] px-1.5">{getModeLabel(p.payment_mode)}</Badge></td>
                                                    <td className="py-2.5 pr-3 text-center text-gray-600 text-xs font-mono">{p.transaction_id || '-'}</td>
                                                    <td className="py-2.5 text-center">
                                                      <div className="flex items-center justify-center gap-1">
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setEditingVendorPayment(p); setVendorFormData({ booking_id: p.booking_id||"", vendor_name: p.vendor_name||"", vendor_type: p.vendor_type||"", city_name: p.city_name||"", total_payable: p.total_payable||"", amount_paid: p.amount_paid||"", payment_mode: p.payment_mode||"", payment_date: p.payment_date?p.payment_date.split("T")[0]:new Date().toISOString().split("T")[0], transaction_id: p.transaction_id||"", bank_name: p.bank_name||"", notes: p.notes||"" }); setShowVendorForm(true); }} data-testid={`vendor-edit-${p.id}`}><Edit className="w-3.5 h-3.5 text-gray-500"/></Button>
                                                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => deleteVendorPayment(p.id)}><Trash2 className="w-3.5 h-3.5 text-red-400"/></Button>
                                                      </div>
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()
                    )}
                </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Agent Payment Dialog */}
        <Dialog open={showAgentForm} onOpenChange={setShowAgentForm}>
            <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl">
                <DialogHeader><DialogTitle>{editingPayment ? 'Edit' : 'Record'} Agent Payment</DialogTitle></DialogHeader>
                <form onSubmit={submitAgentPayment} className="space-y-4 w-full min-w-0">
                    <div className="w-full min-w-0">
                        <Label>Booking</Label>
                        <Select value={agentFormData.booking_id} onValueChange={handleAgentBookingSelect} disabled={!!editingPayment}>
                            <SelectTrigger className="bg-gray-50 border-gray-200 w-full"><SelectValue placeholder="Select Booking" /></SelectTrigger>
                            <SelectContent>
                                {activeBookingsForPayment.map(b => {
                                  const parts = [`#${b.ref}`, b.agent_company || b.agent_name, b.client_name, b.destination].filter(Boolean);
                                  return (
                                    <SelectItem key={b.id} value={b.id} data-testid={`agent-booking-opt-${b.id}`}>{parts.join(" · ")}</SelectItem>
                                  );
                                })}
                                {activeBookingsForPayment.length === 0 && (
                                  <div className="p-3 text-sm text-gray-500 text-center">No pending bookings — all caught up!</div>
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                    {selectedBooking && (
                        <div className="p-2 bg-gray-50 rounded text-sm">
                            <div className="flex justify-between"><span>Total:</span> <span>{formatCurrency(selectedBooking.total_amount || 0)}</span></div>
                            <div className="flex justify-between"><span>Paid:</span> <span className="text-green-400">{formatCurrency(selectedBooking.amount_paid)}</span></div>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 w-full min-w-0">
                        <div className="min-w-0"><Label>Date</Label><Input type="date" value={agentFormData.payment_date} onChange={e => setAgentFormData({...agentFormData, payment_date: e.target.value})} className="bg-gray-50 border-gray-200 w-full" /></div>
                        <div className="min-w-0"><Label>Amount</Label><Input type="number" value={agentFormData.amount_received} onChange={e => setAgentFormData({...agentFormData, amount_received: e.target.value})} className="bg-gray-50 border-gray-200 w-full" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 w-full min-w-0">
                        <div className="min-w-0">
                            <Label>Mode</Label>
                            <Select value={agentFormData.payment_mode} onValueChange={v => setAgentFormData({...agentFormData, payment_mode: v})}>
                                <SelectTrigger className="bg-gray-50 border-gray-200 w-full"><SelectValue placeholder="Select Mode" /></SelectTrigger>
                                <SelectContent>{paymentModes.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="min-w-0"><Label>Bank Name</Label><Input value={agentFormData.bank_name} onChange={e => setAgentFormData({...agentFormData, bank_name: e.target.value})} className="bg-gray-50 border-gray-200 w-full" placeholder="e.g. HDFC" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 w-full min-w-0">
                        <div className="min-w-0"><Label>Transaction ID</Label><Input value={agentFormData.transaction_id} onChange={e => setAgentFormData({...agentFormData, transaction_id: e.target.value})} className="bg-gray-50 border-gray-200 w-full" placeholder="e.g. UPI-123456" /></div>
                        <div className="min-w-0"><Label>Remarks</Label><Input value={agentFormData.notes} onChange={e => setAgentFormData({...agentFormData, notes: e.target.value})} className="bg-gray-50 border-gray-200 w-full" placeholder="Optional notes" /></div>
                    </div>
                    <Button type="submit" className="w-full bg-primary-700" disabled={savingAgentPayment} data-testid="save-agent-payment-btn">
                      {savingAgentPayment ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : "Save"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>

        {/* Agent Payment Download Dialog */}
        <Dialog open={showAgentDlDialog} onOpenChange={open => {
          setShowAgentDlDialog(open);
          if (!open) { setDlFilter("all"); setDlAgentName(""); setDlBookingRef(""); setDlBookingAgent(""); }
        }}>
          <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                <Download className="w-5 h-5 text-emerald-600" /> Download Agent Payments Report
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-1">
              {/* Filter Type Chips */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Filter By</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: "all", label: "ALL" },
                    { key: "agent_wise", label: "AGENT WISE" },
                    { key: "booking_wise", label: "BOOKING ID" },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => { setDlFilter(f.key); setDlAgentName(""); setDlBookingRef(""); setDlBookingAgent(""); }}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        dlFilter === f.key
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                          : "bg-gray-50 text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-700"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Agent Dropdown — AGENT WISE */}
              {dlFilter === "agent_wise" && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Select Agent</p>
                  <Select value={dlAgentName} onValueChange={setDlAgentName}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 w-full" data-testid="dl-agent-select">
                      <SelectValue placeholder="Choose agent..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...new Set(payments.map(p => (p.agent_company || p.agent_name || '').trim()).filter(Boolean))].sort().map(ag => (
                        <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* BOOKING ID — 2-step: pick agent first, then booking */}
              {dlFilter === "booking_wise" && (
                <div className="space-y-3">
                  {/* Step 1: Agent */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Step 1 — Select Agent
                    </p>
                    <Select value={dlBookingAgent} onValueChange={v => { setDlBookingAgent(v); setDlBookingRef(""); }}>
                      <SelectTrigger className="bg-gray-50 border-gray-200 w-full" data-testid="dl-booking-agent-select">
                        <SelectValue placeholder="Choose agent..." />
                      </SelectTrigger>
                      <SelectContent>
                        {[...new Set(payments.map(p => (p.agent_company || p.agent_name || '').trim()).filter(Boolean))].sort().map(ag => (
                          <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Step 2: Booking ID — visible only after agent selected */}
                  {dlBookingAgent && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Step 2 — Select Booking ID
                      </p>
                      <Select value={dlBookingRef} onValueChange={setDlBookingRef}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 w-full" data-testid="dl-booking-select">
                          <SelectValue placeholder="Choose booking..." />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            // Unique bookings for this agent — enrich with guest/date/dest
                            const seen = new Set();
                            return payments
                              .filter(p => (p.agent_company || p.agent_name || '').trim() === dlBookingAgent && p.booking_ref)
                              .filter(p => { if (seen.has(p.booking_ref)) return false; seen.add(p.booking_ref); return true; })
                              .sort((a, b) => String(a.booking_ref).localeCompare(String(b.booking_ref)))
                              .map(p => {
                                const dest = p.destination ? `${p.nights ? p.nights + 'N ' : ''}${p.destination}` : '';
                                const tDate = p.travel_date ? formatDate(p.travel_date) : '';
                                const label = [`#${p.booking_ref}`, p.client_name, dest, tDate].filter(Boolean).join(' · ');
                                return (
                                  <SelectItem key={p.booking_ref} value={p.booking_ref}>
                                    {label}
                                  </SelectItem>
                                );
                              });
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Date Range */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Date Range (Optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">From</p>
                    <Input
                      type="date"
                      value={dlDateFrom}
                      onChange={e => setDlDateFrom(e.target.value)}
                      className="bg-gray-50 border-gray-200 w-full text-sm"
                      data-testid="dl-date-from"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">To</p>
                    <Input
                      type="date"
                      value={dlDateTo}
                      onChange={e => setDlDateTo(e.target.value)}
                      className="bg-gray-50 border-gray-200 w-full text-sm"
                      data-testid="dl-date-to"
                    />
                  </div>
                </div>
              </div>

              {/* Preview count */}
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-2.5 flex items-center justify-between">
                <span className="text-sm text-emerald-700">Records to export</span>
                <span className="text-sm font-bold text-emerald-800">{getAgentDownloadData().length}</span>
              </div>

              {/* Download Buttons */}
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Download As</p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 gap-2 border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold"
                    onClick={() => { const d = getAgentDownloadData(); if (!d.length) return; downloadAgentExcel(d); setShowAgentDlDialog(false); }}
                    data-testid="agent-dl-dialog-excel"
                  >
                    <Download className="w-4 h-4" /> Download Excel
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 gap-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-semibold"
                    onClick={() => { const d = getAgentDownloadData(); if (!d.length) return; downloadAgentPDF(d); setShowAgentDlDialog(false); }}
                    data-testid="agent-dl-dialog-pdf"
                  >
                    <FileText className="w-4 h-4" /> Download PDF
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Vendor Payment Dialog */}
        <Dialog open={showVendorForm} onOpenChange={(open) => { setShowVendorForm(open); if (!open) { setEditingVendorPayment(null); setBookingVendorThreads([]); setVendorChoice("new"); } }}>
            <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl">
                <DialogHeader><DialogTitle>{editingVendorPayment ? 'Edit' : 'Record'} Vendor Payment</DialogTitle></DialogHeader>
                <form onSubmit={submitVendorPayment} className="space-y-4">
                    <div>
                        <Label>Booking ID</Label>
                        <Select value={vendorFormData.booking_id} onValueChange={handleVendorBookingSelect} disabled={!!editingVendorPayment}>
                            <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="vendor-booking-select"><SelectValue placeholder="Select Booking" /></SelectTrigger>
                            <SelectContent>
                                {activeBookingsForPayment.map(b => {
                                  const parts = [`#${b.ref}`, b.agent_company || b.agent_name, b.client_name, b.destination].filter(Boolean);
                                  return (
                                    <SelectItem key={b.id} value={b.id} data-testid={`vendor-booking-opt-${b.id}`}>{parts.join(" · ")}</SelectItem>
                                  );
                                })}
                                {activeBookingsForPayment.length === 0 && (
                                  <div className="p-3 text-sm text-gray-500 text-center">No pending bookings — all caught up!</div>
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Vendor selector — pick existing vendor (thread) or add new */}
                    {vendorFormData.booking_id && !editingVendorPayment && (
                      <div>
                        <Label>Vendor</Label>
                        <Select value={vendorChoice} onValueChange={handleVendorChoice}>
                          <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="vendor-choice-select"><SelectValue placeholder="Pick vendor" /></SelectTrigger>
                          <SelectContent>
                            {bookingVendorThreads.length > 0 && bookingVendorThreads.map(v => (
                              <SelectItem key={v.vendor_name} value={v.vendor_name} data-testid={`vendor-thread-opt-${v.vendor_name}`}>
                                {v.vendor_name} · Bal ₹{Number(v.balance_amount || 0).toLocaleString()}
                              </SelectItem>
                            ))}
                            <SelectItem value="new" data-testid="vendor-new-opt">+ Add new vendor</SelectItem>
                          </SelectContent>
                        </Select>
                        {currentVendorThread && (
                          <div className="mt-2 grid grid-cols-3 gap-2 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3 text-sm" data-testid="vendor-thread-summary">
                            <div><div className="text-[11px] uppercase text-gray-500">Total Payable</div><div className="font-semibold text-gray-800">{formatCurrency(currentVendorThread.total_payable)}</div></div>
                            <div><div className="text-[11px] uppercase text-gray-500">Advance Paid</div><div className="font-semibold text-emerald-700">{formatCurrency(currentVendorThread.advance_paid)}</div></div>
                            <div><div className="text-[11px] uppercase text-gray-500">Balance</div><div className="font-semibold text-rose-700">{formatCurrency(currentVendorThread.balance_amount)}</div></div>
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <Label>Vendor Name {currentVendorThread && <span className="ml-1 text-[11px] text-gray-400">(continuing thread)</span>}</Label>
                      <Input
                        value={vendorFormData.vendor_name}
                        onChange={e => setVendorFormData({...vendorFormData, vendor_name: e.target.value})}
                        className="bg-gray-50 border-gray-200"
                        placeholder="e.g. Hilton Goa"
                        disabled={!!currentVendorThread}
                        data-testid="vendor-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Vendor Type</Label>
                        <Select value={vendorFormData.vendor_type} onValueChange={v => setVendorFormData({...vendorFormData, vendor_type: v})}>
                          <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="vendor-type-select">
                            <SelectValue placeholder="Select Type" />
                          </SelectTrigger>
                          <SelectContent>
                            {["Hotel", "Taxi", "DMC", "Visa", "Flight", "Entry Ticket"].map(t => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>City Name</Label>
                        <Input
                          value={vendorFormData.city_name}
                          onChange={e => setVendorFormData({...vendorFormData, city_name: e.target.value})}
                          className="bg-gray-50 border-gray-200"
                          placeholder="e.g. Leh, Goa"
                          data-testid="vendor-city-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Total Payable {currentVendorThread && <span className="ml-1 text-[11px] text-gray-400">(locked from thread)</span>}</Label>
                          <Input
                            type="number"
                            value={vendorFormData.total_payable}
                            onChange={e => setVendorFormData({...vendorFormData, total_payable: e.target.value})}
                            className="bg-gray-50 border-gray-200"
                            placeholder="Total bill amount"
                            disabled={!!currentVendorThread}
                            data-testid="vendor-total-input"
                          />
                        </div>
                        <div><Label>Amount Paid (this transaction)</Label><Input type="number" value={vendorFormData.amount_paid} onChange={e => setVendorFormData({...vendorFormData, amount_paid: e.target.value})} className="bg-gray-50 border-gray-200" placeholder="Paying now" data-testid="vendor-amount-paid-input" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Payment Date</Label><Input type="date" value={vendorFormData.payment_date} onChange={e => setVendorFormData({...vendorFormData, payment_date: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                        <div>
                             <Label>Mode</Label>
                            <Select value={vendorFormData.payment_mode} onValueChange={v => setVendorFormData({...vendorFormData, payment_mode: v})}>
                                <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue placeholder="Select Mode" /></SelectTrigger>
                                <SelectContent>{paymentModes.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Bank Name</Label><Input value={vendorFormData.bank_name} onChange={e => setVendorFormData({...vendorFormData, bank_name: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                        <div><Label>Transaction ID</Label><Input value={vendorFormData.transaction_id} onChange={e => setVendorFormData({...vendorFormData, transaction_id: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                    </div>
                    <Button type="submit" className="w-full bg-primary-700" disabled={savingVendorPayment} data-testid="save-vendor-payment-btn">
                      {savingVendorPayment ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>) : "Save Vendor Payment"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>

        {/* Vendor Payment Download Dialog */}
        <Dialog open={showVendorDlDialog} onOpenChange={open => {
          setShowVendorDlDialog(open);
          if (!open) { setVDlFilter("all"); setVDlVendorName(""); setVDlBookingRef(""); setVDlBookingAgent(""); }
        }}>
          <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold flex items-center gap-2">
                <Download className="w-5 h-5 text-amber-600" /> Download Vendor Payments Report
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 py-1">
              {/* Filter Type Chips */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Filter By</p>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { key: "all", label: "ALL" },
                    { key: "vendor_wise", label: "VENDOR WISE" },
                    { key: "booking_wise", label: "BOOKING ID" },
                  ].map(f => (
                    <button
                      key={f.key}
                      onClick={() => { setVDlFilter(f.key); setVDlVendorName(""); setVDlBookingRef(""); setVDlBookingAgent(""); }}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                        vDlFilter === f.key
                          ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                          : "bg-gray-50 text-gray-600 border-gray-200 hover:border-amber-400 hover:text-amber-700"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Vendor Dropdown — VENDOR WISE */}
              {vDlFilter === "vendor_wise" && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Select Vendor</p>
                  <Select value={vDlVendorName} onValueChange={setVDlVendorName}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 w-full" data-testid="vdl-vendor-select">
                      <SelectValue placeholder="Choose vendor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...new Set(vendorPayments.map(p => (p.vendor_name || '').trim()).filter(Boolean))].sort().map(vn => (
                        <SelectItem key={vn} value={vn}>{vn}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* BOOKING ID — 2-step: pick agent first, then booking */}
              {vDlFilter === "booking_wise" && (
                <div className="space-y-3">
                  {/* Step 1: Agent */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Step 1 — Select Agent
                    </p>
                    <Select value={vDlBookingAgent} onValueChange={v => { setVDlBookingAgent(v); setVDlBookingRef(""); }}>
                      <SelectTrigger className="bg-gray-50 border-gray-200 w-full" data-testid="vdl-booking-agent-select">
                        <SelectValue placeholder="Choose agent..." />
                      </SelectTrigger>
                      <SelectContent>
                        {[...new Set(vendorPayments.map(p => (p.agent_company || p.agent_name || '').trim()).filter(Boolean))].sort().map(ag => (
                          <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Step 2: Booking ID */}
                  {vDlBookingAgent && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Step 2 — Select Booking ID
                      </p>
                      <Select value={vDlBookingRef} onValueChange={setVDlBookingRef}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 w-full" data-testid="vdl-booking-select">
                          <SelectValue placeholder="Choose booking..." />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            const seen = new Set();
                            return vendorPayments
                              .filter(p => (p.agent_company || p.agent_name || '').trim() === vDlBookingAgent && p.booking_ref)
                              .filter(p => { if (seen.has(p.booking_ref)) return false; seen.add(p.booking_ref); return true; })
                              .sort((a, b) => String(a.booking_ref).localeCompare(String(b.booking_ref)))
                              .map(p => {
                                const dest = p.destination ? `${p.nights ? p.nights + 'N ' : ''}${p.destination}` : '';
                                const tDate = p.travel_date ? formatDate(p.travel_date) : '';
                                const label = [`#${p.booking_ref}`, p.client_name, dest, tDate].filter(Boolean).join(' · ');
                                return (
                                  <SelectItem key={p.booking_ref} value={p.booking_ref}>
                                    {label}
                                  </SelectItem>
                                );
                              });
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Date Range */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Date Range (Optional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">From</p>
                    <Input type="date" value={vDlDateFrom} onChange={e => setVDlDateFrom(e.target.value)} className="bg-gray-50 border-gray-200 w-full text-sm" data-testid="vdl-date-from" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">To</p>
                    <Input type="date" value={vDlDateTo} onChange={e => setVDlDateTo(e.target.value)} className="bg-gray-50 border-gray-200 w-full text-sm" data-testid="vdl-date-to" />
                  </div>
                </div>
              </div>

              {/* Preview count */}
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-2.5 flex items-center justify-between">
                <span className="text-sm text-amber-700">Records to export</span>
                <span className="text-sm font-bold text-amber-800">{getVendorDownloadData().length}</span>
              </div>

              {/* Download Buttons */}
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Download As</p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 gap-2 border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold"
                    onClick={() => { const d = getVendorDownloadData(); if (!d.length) return; downloadVendorExcel(d); setShowVendorDlDialog(false); }}
                    data-testid="vendor-dl-dialog-excel"
                  >
                    <Download className="w-4 h-4" /> Download Excel
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 gap-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-semibold"
                    onClick={() => { const d = getVendorDownloadData(); if (!d.length) return; downloadVendorPDF(d); setShowVendorDlDialog(false); }}
                    data-testid="vendor-dl-dialog-pdf"
                  >
                    <FileText className="w-4 h-4" /> Download PDF
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </DashboardLayout>
  );
};

export default AdminPayments;
